import { Component, Input, OnChanges } from 'angular-ts-decorators';

import { fetchGroupPrice } from '@gn-audio/core/api/pricing/fetch-group-price';
import { fetchProductPrice } from '@gn-audio/core/api/pricing/fetch-product-price';
import { IFromPrice, IPrice, ISFCCGroupPrice, ISFCCProductPrice } from '@gn-audio/core/api/pricing/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { fetchGroupAccessories } from '@gn-audio/core/api/product-catalogue/fetch-group-accessories';
import { fetchGroupAttributes } from '@gn-audio/core/api/product-catalogue/fetch-group-attributes';
import { IAttribute, IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models';
import { AvailabilityState, ProductAttributeKey } from '@gn-audio/core/common/enums';

import { ICustomizerApiFilterOption } from '../../models';
import { IHeroCustomizerAccessory } from '../models';

import template from './hero-customizer-v3-accessories.component.html';
import { accessoryTempCodeName } from '../constants/accessory-temp-code-name';
import { IUrlParamKeyValuePair } from '@gn-audio/core/common/models/url-param-key-value-pair';

@Component({
  selector: 'heroCustomizerV3Accessories',
  template,
})
export class HeroCustomizerV3AccessoriesComponent implements OnChanges {
  public static $inject: ReadonlyArray<string> = ['$q'];

  @Input() public group?: IProductWithAttributes;
  @Input() public accessoryTypes?: ICustomizerApiFilterOption[];
  @Input() public showAccessories?: boolean;

  @Input('&') public readonly onSelectedAccessoryChange?: (
    args: { selectedAccessories: IHeroCustomizerAccessory[] }
  ) => void;

  public availableAccessories?: IHeroCustomizerAccessory[];
  public queryParameters: IUrlParamKeyValuePair[] = [
    {
      key: 'tempCode',
      value: accessoryTempCodeName
    }
  ]
  
  private groupAvailabilityState: AvailabilityState = AvailabilityState.WhereToBuy;

  constructor(private readonly $q: ng.IQService) { }

  public ngOnChanges() {
    this.availableAccessories = [];

    if (this.group) {
      this.$q.when(fetchGroupAccessories(this.group.groupId))
        .then((accessories: IHeroCustomizerAccessory[]) => {
          this.getAvailableAccessories(accessories);
        });

      this.$q.when(fetchGroupPrice(this.group.groupId))
        .then((price: IFromPrice | ISFCCGroupPrice | null) => {
          this.groupAvailabilityState = getAvailabilityState(price);
        });
    }
  }

  private getAvailableAccessories(accessories: IHeroCustomizerAccessory[]) {
    let state: AvailabilityState = AvailabilityState.WhereToBuy;

    accessories.forEach((accessory: IHeroCustomizerAccessory) => {
      this.$q.when(fetchGroupAttributes(accessory.groupId))
        .then((product: IProductWithAttributes) => {
          if (this.isSelectedAccessoryType(product)) {
            this.$q.when(fetchProductPrice(accessory.productId, this.queryParameters))
              .then((price: IPrice | ISFCCProductPrice | null) => {
                state = getAvailabilityState(price);
                if (state <= AvailabilityState.BackOrder) {
                  accessory.price = price;
                  accessory.isSelected = false;
                  this.availableAccessories.push(accessory);
                }
              });
          }
        });
    });
  }

  public get isVisible(): boolean {
    return Array.isArray(this.availableAccessories) && this.availableAccessories.length > 0 && this.showAccessories && this.isGroupAvailable();
  }

  private isGroupAvailable(): boolean {
    return this.groupAvailabilityState <= AvailabilityState.BackOrder;
  }

  private isSelectedAccessoryType(product: IProductWithAttributes): boolean {
    let attributes: IAttribute[];
    attributes = product.attributes.filter((a: IAttribute) => a.key === ProductAttributeKey.MetaAccessoryType);
    return !!attributes.length && attributes
      .every(
        (a: IAttribute): boolean =>
          this.accessoryTypes.some(
            (type: ICustomizerApiFilterOption): boolean => {
              return a.valueKey === type.value;
            },
          ),
      );
  }

  public onAccessoryClicked(accessory: IHeroCustomizerAccessory): void {
    accessory.isSelected = !accessory.isSelected;

    const selectedAccessories: IHeroCustomizerAccessory[] = this.availableAccessories
      ?.filter((a: IHeroCustomizerAccessory) => a.isSelected) || [];
    if (this.onSelectedAccessoryChange) {
      this.onSelectedAccessoryChange({ selectedAccessories });
    }
  }
}
